'use client';

import React, { useState, useCallback } from 'react';
import { FaCalendarAlt } from 'react-icons/fa';
import { Event } from '@benzinga/events-manager';
import { ErrorBoundary } from '@benzinga/core-ui';
import { BzImage, defaultImageLoader } from '@benzinga/image';
import { sanitizeHTML } from '@benzinga/frontend-utils';
import { BlockLayout } from './BlockLayout';

interface EventCardProps {
  event: Event;
}

const EventCard = React.memo(({ event }: EventCardProps) => (
  <a href={event.acf.learn_more} target="_blank" rel="noreferrer" className="block group">
    <div className="w-full h-[250px] rounded-lg overflow-hidden relative bg-gray-900">
      {event.acf.marketing_image && (
        <>
          <div
            className="absolute inset-0 scale-125"
            style={{
              backgroundImage: `url(${event.acf.marketing_image})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              filter: 'blur(30px)',
            }}
          />
          <BzImage
            alt={event.title.rendered}
            src={event.acf.marketing_image}
            layout="fill"
            loader={props =>
              defaultImageLoader({
                ...props,
                width: 400,
                resizeViaQueryParams: true,
              })
            }
            className="w-full h-full object-contain relative z-10"
          />
        </>
      )}
    </div>
    <div className="mt-3 h-[90px]">
      <p className="text-xs text-gray-500 flex items-center gap-1.5 mb-1.5">
        <FaCalendarAlt size={10} />
        <span className="truncate">{event.acf.date_and_location}</span>
      </p>
      <h3
        className="font-semibold text-sm text-gray-900 leading-tight group-hover:text-blue-600 transition-colors line-clamp-2"
        dangerouslySetInnerHTML={{ __html: sanitizeHTML(event.title.rendered) }}
      />
      <span className="inline-block mt-2 text-xs font-semibold text-blue-600 group-hover:underline">
        Register Now →
      </span>
    </div>
  </a>
));

interface PaginationDotsProps {
  count: number;
  currentIndex: number;
  onSelect: (index: number) => void;
}

const PaginationDots = React.memo(({ count, currentIndex, onSelect }: PaginationDotsProps) => (
  <div className="flex items-center justify-center gap-1.5 mt-4 pt-3 border-t border-gray-100" role="tablist">
    {Array.from({ length: count }).map((_, index) => (
      <button
        key={index}
        onClick={() => onSelect(index)}
        aria-label={`Go to event ${index + 1} of ${count}`}
        aria-selected={index === currentIndex}
        role="tab"
        className={`w-2 h-2 rounded-full transition-all duration-200 ${
          index === currentIndex ? 'bg-blue-500 w-4' : 'bg-gray-300 hover:bg-gray-400'
        }`}
      />
    ))}
  </div>
));

interface EventsSideWidgetProps {
  events: Event[];
}

export const EventsSideWidget = ({ events }: EventsSideWidgetProps) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const currentEvent = events[currentIndex];

  const handleSelectIndex = useCallback((index: number) => {
    setCurrentIndex(index);
  }, []);

  const renderContent = () => {
    if (!currentEvent) {
      return (
        <div className="flex flex-col items-center justify-center h-[270px] text-center">
          <FaCalendarAlt size={32} className="text-gray-300 mb-3" />
          <p className="text-gray-500 text-sm">No upcoming events at this time.</p>
        </div>
      );
    }

    return <EventCard event={currentEvent} />;
  };

  return (
    <ErrorBoundary name="events-side-widget">
      <BlockLayout title="UPCOMING EVENTS" actionLabel="View All" actionLink="/events" showChevron variant="default">
        {renderContent()}
        {events.length > 1 && (
          <PaginationDots count={events.length} currentIndex={currentIndex} onSelect={handleSelectIndex} />
        )}
      </BlockLayout>
    </ErrorBoundary>
  );
};

export default EventsSideWidget;
